import React, { useMemo, useState } from 'react';
import { Row, Col, Card, Button, Table, Space, Typography, Tag, Statistic, Select } from 'antd';
import { CreditCardOutlined, DownloadOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const WhiteLabelBilling = () => {
  const [status, setStatus] = useState('all');

  // Mock subscription and billing data
  const subscription = {
    plan: 'Agency Pro',
    price: 2499,
    cycle: 'Monthly',
    seats: 25,
    nextInvoiceDate: '2025-09-01',
    status: 'Active'
  };

  const paymentMethods = [
    { id: 'pm_1', brand: 'Visa', last4: '4242', exp: '12/27', primary: true },
    { id: 'pm_2', brand: 'Mastercard', last4: '4444', exp: '05/26', primary: false },
  ];

  const invoices = useMemo(() => ([
    { key: 'inv_1005', date: '2025-08-01', amount: 2499, status: 'Paid' },
    { key: 'inv_1004', date: '2025-07-01', amount: 2499, status: 'Paid' },
    { key: 'inv_1003', date: '2025-06-01', amount: 2499, status: 'Paid' },
    { key: 'inv_1002', date: '2025-05-01', amount: 2499, status: 'Paid' },
    { key: 'inv_1001', date: '2025-04-01', amount: 2499, status: 'Paid' },
  ]), []);

  const filteredInvoices = useMemo(() => (
    invoices.filter(i => status === 'all' || i.status.toLowerCase() === status)
  ), [invoices, status]);

  const invoiceColumns = [
    { title: 'Invoice #', dataIndex: 'key', key: 'key' },
    { title: 'Date', dataIndex: 'date', key: 'date', width: 140 },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, render: (v) => `$${v.toLocaleString()}` },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => <Tag color={s === 'Paid' ? 'green' : 'gold'}>{s}</Tag> },
    { title: 'Actions', key: 'actions', width: 140, render: (_, r) => (
      <Space>
        <Button size="small" icon={<DownloadOutlined />}>PDF</Button>
        <Button size="small" type="link">Details</Button>
      </Space>
    )},
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Agency Billing</Title>
          <Text type="secondary">Manage subscription, invoices, and payment methods.</Text>
        </div>

        {/* Subscription summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Subscription" extra={<Button icon={<EditOutlined />}>Change Plan</Button>}>
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12} md={8}>
                  <Statistic title="Plan" value={subscription.plan} />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic title="Price" value={subscription.price} prefix="$" suffix={` / ${subscription.cycle}`} />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic title="Seats" value={subscription.seats} />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic title="Next Invoice" value={subscription.nextInvoiceDate} />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Space direction="vertical">
                    <Text type="secondary">Status</Text>
                    <Tag color="green">{subscription.status}</Tag>
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Payment Methods" extra={<Button icon={<PlusOutlined />}>Add Card</Button>}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {paymentMethods.map(pm => (
                  <div key={pm.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                      <CreditCardOutlined />
                      <Text>{pm.brand} •••• {pm.last4} (exp {pm.exp})</Text>
                    </Space>
                    <Space>
                      {pm.primary ? <Tag color="blue">Primary</Tag> : <Button size="small" type="link">Make Primary</Button>}
                      <Button size="small" type="link">Remove</Button>
                    </Space>
                  </div>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Invoices */}
        <Card
          title="Invoices"
          extra={(
            <Space>
              <Select value={status} onChange={setStatus} style={{ width: 160 }}>
                <Select.Option value="all">All</Select.Option>
                <Select.Option value="paid">Paid</Select.Option>
                <Select.Option value="due">Due</Select.Option>
              </Select>
              <Button icon={<DownloadOutlined />}>Export CSV</Button>
            </Space>
          )}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            rowKey="key"
            columns={invoiceColumns}
            dataSource={filteredInvoices}
            pagination={{ pageSize: 8 }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default WhiteLabelBilling;
